<script setup>
import { formatarDataBR } from '@/utils/useDataBR'

defineProps({
  itemDetalhes: { type: Object, required: true },
  listas: { type: Object, required: true },
  formatarValor: { type: Function, default: (v) => v },
  getTextoLista: { type: Function, default: (l, id) => id || '-' },
  formatarMoedaComSigla: { type: Function, default: (v, m) => `${v} ${m}` }
})
</script>

<template>
  <!-- Resumo em destaque (primeiro) -->
  <div class="bg-body-tertiary border rounded p-2 mb-3">
    <small class="text-body-secondary d-block lh-1 mb-1 fw-bold">
      <i class="bi bi-chat-quote me-1 text-info"></i> Resumo
    </small>
    <p class="mb-0 text-body-secondary small">{{ itemDetalhes.resumoAprovacao || 'Nenhum resumo registrado.' }}</p>
    <small v-if="itemDetalhes.nomeUsuarioCriacao" class="text-body-secondary d-block mt-1 small">
      <i class="bi bi-person-fill me-1 text-info"></i> Criado por: <span class="fw-medium">{{ itemDetalhes.nomeUsuarioCriacao }}</span>
    </small>
  </div>

  <div class="row g-2 mb-3">
    <div class="col-md-3">
      <small class="text-body-secondary d-block lh-1 mb-1">Fornecedor</small>
      <span class="fw-medium small">{{ itemDetalhes.nomeFornecedor || '-' }}</span>
    </div>
    <div class="col-md-3">
      <small class="text-body-secondary d-block lh-1 mb-1">Filial</small>
      <span class="small">{{ itemDetalhes.nomeFilial || '-' }}</span>
    </div>
    <div class="col-md-3">
      <small class="text-body-secondary d-block lh-1 mb-1">Operação</small>
      <span class="small">{{ itemDetalhes.nomeOperacao || '-' }}</span>
    </div>
    <div class="col-md-3">
      <small class="text-body-secondary d-block lh-1 mb-1">Status</small>
      <span class="badge badge-ghost-info">{{ getTextoLista(listas.LstStatusOrdemCompra, itemDetalhes.lstStatusOrdemCompra) }}</span>
    </div>
    <div class="col-md-3">
      <small class="text-body-secondary d-block lh-1 mb-1">Data Emissão</small>
      <span class="small">{{ formatarDataBR(itemDetalhes.dataEmissao) }}</span>
    </div>
    <div class="col-md-3">
      <small class="text-body-secondary d-block lh-1 mb-1">Previsão Entrega</small>
      <span class="small">{{ itemDetalhes.dataPrevisaoEntrega ? formatarDataBR(itemDetalhes.dataPrevisaoEntrega) : '-' }}</span>
    </div>
    <div class="col-md-3">
      <small class="text-body-secondary d-block lh-1 mb-1">Incoterm</small>
      <span class="small">{{ getTextoLista(listas.LstIncoterm, itemDetalhes.lstIncoterm) }}</span>
    </div>
    <div class="col-md-3">
      <small class="text-body-secondary d-block lh-1 mb-1">Forma Pagamento</small>
      <span class="small">{{ getTextoLista(listas.LstFormaPagamento, itemDetalhes.lstFormaPagamento) }}</span>
    </div>
    <div class="col-md-3">
      <small class="text-body-secondary d-block lh-1 mb-1">Condição Pagamento</small>
      <span class="small">{{ itemDetalhes.idCondicaoPagamento ? (itemDetalhes.idCondicaoPagamento + ' - ' + itemDetalhes.nomeCondicaoPagamento) : '-' }}</span>
    </div>
    <div class="col-md-3">
      <small class="text-body-secondary d-block lh-1 mb-1">Valor Total</small>
      <span class="fw-medium small">{{ formatarMoedaComSigla(itemDetalhes.valorTotalNegociado, itemDetalhes.lstMoedaPadrao) }}</span>
    </div>
    <div class="col-md-3">
      <small class="text-body-secondary d-block lh-1 mb-1">Grupo Aprovador</small>
      <span class="small">{{ itemDetalhes.nomeGrupoAprovador || '-' }}</span>
    </div>
  </div>

  <!-- Tabela completa de itens -->
  <div v-if="itemDetalhes.itens && itemDetalhes.itens.length > 0" class="mb-2">
    <small class="fw-bold text-body-emphasis d-block mb-1">
      <i class="bi bi-list-check me-1 text-info"></i> Itens ({{ itemDetalhes.itens.length }})
    </small>
    <div class="table-responsive itens-scroll-wrapper">
      <table class="table table-sm table-hover align-middle mb-0">
        <thead class="table-light">
          <tr>
            <th style="font-size: 0.75rem;">Produto</th>
            <th style="font-size: 0.75rem;">Lote</th>
            <th class="text-end" style="font-size: 0.75rem;">Qtd Forn</th>
            <th class="text-center" style="font-size: 0.75rem;">Un</th>
            <th class="text-end" style="font-size: 0.75rem;">Vlr Total</th>
            <th style="font-size: 0.75rem;">Observação</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(item, idx) in itemDetalhes.itens" :key="idx">
            <td>
              <span class="fw-medium text-body-emphasis small">{{ item.nomeProduto || item.sku || 'Produto #' + item.idProduto }}</span>
              <small v-if="item.sku && item.nomeProduto" class="d-block text-body-secondary" style="font-size: 0.65rem;">SKU: {{ item.sku }}</small>
              <small v-else-if="!item.sku && !item.nomeProduto && item.idProduto" class="d-block text-body-secondary" style="font-size: 0.65rem;">ID: {{ item.idProduto }}</small>
            </td>
            <td><span class="small">{{ item.numeroLoteFornecedor || '-' }}</span></td>
            <td class="text-end fw-medium small">{{ formatarValor(item.quantidadePedidaUnFornecedor) }}</td>
            <td class="text-center small">{{ getTextoLista(listas.LstUnidadeMedidaFornecedor, item.lstUnidadeMedidaFornecedor) }}</td>
            <td class="text-end fw-medium small">{{ formatarValor(item.valorTotalNegociado) }}</td>
            <td class="small text-body-secondary">{{ item.observacao || '-' }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
  <div v-else class="text-center text-body-secondary small py-2">
    Nenhum item registrado.
  </div>
</template>

<style scoped>
.itens-scroll-wrapper {
  max-height: 220px;
  overflow-y: auto;
}

.itens-scroll-wrapper::-webkit-scrollbar {
  width: 6px;
}

.itens-scroll-wrapper::-webkit-scrollbar-thumb {
  background-color: var(--bs-border-color);
  border-radius: 3px;
}
</style>
